import * as THREE from 'three';
import type { AssemblyMateComponentRotationAxis } from '../../mate';

/**
 * 装配体受约束旋转投影器。
 *
 * 该类只处理 cad-core 内的几何投影与世界矩阵构建，不读取 UI 状态，也不直接调用求解器。
 * 数值旋转和视口拖拽旋转都应复用这里的投影规则，避免不同入口对转动副剩余自由度理解不一致。
 */
export class AssemblyConstrainedRotationProjector {
  /** 可认为无效的轴向或角度阈值。 */
  private static readonly EPSILON = 1e-12;

  /** Mate 图解析出的转轴原点复用缓存。 */
  private readonly axisOrigin = new THREE.Vector3();

  /** Mate 图解析出的转轴方向复用缓存。 */
  private readonly axisDirection = new THREE.Vector3();

  /** Delta XYZ 输入转成旋转向量时的复用缓存。 */
  private readonly eulerRotationVector = new THREE.Vector3();

  /** 四元数输入转成轴角时的复用轴向缓存。 */
  private readonly quaternionAxis = new THREE.Vector3();

  /** 构建转轴旋转矩阵时的复用四元数。 */
  private readonly axisQuaternion = new THREE.Quaternion();

  /** 转到轴原点的平移矩阵。 */
  private readonly toAxisOriginMatrix = new THREE.Matrix4();

  /** 从轴原点转回世界位置的平移矩阵。 */
  private readonly fromAxisOriginMatrix = new THREE.Matrix4();

  /** 绕 Mate 剩余轴旋转的矩阵。 */
  private readonly axisRotationMatrix = new THREE.Matrix4();

  /**
   * 将 Delta XYZ 旋转输入投影到 Mate 图给出的剩余转轴。
   *
   * @param deltaRotation 以角度为单位的 XYZ 欧拉增量。
   * @param rotationAxis Mate 图解析出的剩余转轴。
   * @returns 绕剩余转轴的有符号弧度；无有效转轴时返回 0。
   */
  public projectEulerDeltaToAxisAngle(
    deltaRotation: readonly [number, number, number],
    rotationAxis: AssemblyMateComponentRotationAxis,
  ): number {
    if (!this.readRotationAxis(rotationAxis)) {
      return 0;
    }

    this.eulerRotationVector.set(
      THREE.MathUtils.degToRad(deltaRotation[0]),
      THREE.MathUtils.degToRad(deltaRotation[1]),
      THREE.MathUtils.degToRad(deltaRotation[2]),
    );
    return this.eulerRotationVector.dot(this.axisDirection);
  }

  /**
   * 将视口拖拽产生的世界空间四元数投影到 Mate 图给出的剩余转轴。
   *
   * @param deltaQuaternion 世界空间旋转增量。
   * @param rotationAxis Mate 图解析出的剩余转轴。
   * @returns 绕剩余转轴的有符号弧度；无有效转轴或无有效输入时返回 0。
   */
  public projectQuaternionDeltaToAxisAngle(
    deltaQuaternion: THREE.Quaternion,
    rotationAxis: AssemblyMateComponentRotationAxis,
  ): number {
    if (!this.readRotationAxis(rotationAxis)) {
      return 0;
    }

    this.axisQuaternion.copy(deltaQuaternion).normalize();
    const vectorLength = Math.sqrt(
      this.axisQuaternion.x * this.axisQuaternion.x
      + this.axisQuaternion.y * this.axisQuaternion.y
      + this.axisQuaternion.z * this.axisQuaternion.z,
    );
    if (vectorLength <= AssemblyConstrainedRotationProjector.EPSILON) {
      return 0;
    }

    let angle = 2 * Math.atan2(vectorLength, this.axisQuaternion.w);
    if (angle > Math.PI) {
      angle -= Math.PI * 2;
    }
    this.quaternionAxis.set(
      this.axisQuaternion.x / vectorLength,
      this.axisQuaternion.y / vectorLength,
      this.axisQuaternion.z / vectorLength,
    );
    return angle * this.quaternionAxis.dot(this.axisDirection);
  }

  /**
   * 构建绕 Mate 剩余转轴旋转的世界空间 delta 矩阵。
   *
   * @param rotationAxis Mate 图解析出的剩余转轴。
   * @param signedAngle 绕剩余转轴的有符号弧度。
   * @param targetMatrix 写入结果的目标矩阵。
   * @returns 是否成功写入有效旋转矩阵。
   */
  public buildAxisRotationDeltaMatrix(
    rotationAxis: AssemblyMateComponentRotationAxis,
    signedAngle: number,
    targetMatrix: THREE.Matrix4,
  ): boolean {
    if (
      Math.abs(signedAngle) <= AssemblyConstrainedRotationProjector.EPSILON
      || !this.readRotationAxis(rotationAxis)
    ) {
      return false;
    }

    this.axisQuaternion.setFromAxisAngle(this.axisDirection, signedAngle);
    this.toAxisOriginMatrix.makeTranslation(
      -this.axisOrigin.x,
      -this.axisOrigin.y,
      -this.axisOrigin.z,
    );
    this.axisRotationMatrix.makeRotationFromQuaternion(this.axisQuaternion);
    this.fromAxisOriginMatrix.makeTranslation(
      this.axisOrigin.x,
      this.axisOrigin.y,
      this.axisOrigin.z,
    );
    targetMatrix
      .copy(this.fromAxisOriginMatrix)
      .multiply(this.axisRotationMatrix)
      .multiply(this.toAxisOriginMatrix);
    return true;
  }

  /**
   * 读取并校验 Mate 图给出的转轴。
   *
   * @param rotationAxis Mate 图解析出的剩余转轴。
   * @returns 转轴方向有效时返回 true。
   */
  private readRotationAxis(rotationAxis: AssemblyMateComponentRotationAxis): boolean {
    this.axisOrigin.fromArray(rotationAxis.origin);
    this.axisDirection.fromArray(rotationAxis.direction);
    if (this.axisDirection.lengthSq() <= AssemblyConstrainedRotationProjector.EPSILON ** 2) {
      return false;
    }
    this.axisDirection.normalize();
    return true;
  }
}
